import React from "react";
import { useSelector } from "react-redux";
import {
  Tree,
  Card,
  Divider,
  Empty,
  Tag,
  Space,
  Typography,
  Badge,
  Alert,
} from "antd";

import {
  ApartmentOutlined,
  DeploymentUnitOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const getIcon = (level, status) => {
  const baseIcon = {
    SSCC: <ApartmentOutlined />,
    CS: <DeploymentUnitOutlined />,
  }[level] || <InboxOutlined />;

  if (status === 'active') {
    return (
      <Badge status="success" offset={[-5, 0]}>
        {baseIcon}
      </Badge>
    );
  }
  return baseIcon;
};

const getTagColor = (level) => {
  switch (level) {
    case "SSCC":
      return "blue";
    case "CS":
      return "purple";
    default:
      return "green";
  }
};

const getStatusTag = (status) => {
  if (status === 'active') {
    return <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>;
  }
  if (status === 'unlinked') {
    return <Tag color="warning" icon={<InfoCircleOutlined />}>Unlinked</Tag>;
  }
  return <Tag color="default" icon={<CloseCircleOutlined />}>{status}</Tag>;
};

const buildTreeData = (nodes = [], level = 0) => {
  return nodes.map((node) => ({
    key: node.id || node.sscc_code,
    title: (
      <Space>
        {getIcon(node.level, node.status)}
        <Text strong={level === 0}>{node.sscc_code}</Text>
        <Tag color={getTagColor(node.level)}>
          {node.level || 'CS'}
        </Tag>
        {node.status && getStatusTag(node.status)}
        {node.id && (
          <Text type="secondary" style={{ fontSize: '10px' }}>
            ID: {node.id.substring(0, 8)}...
          </Text>
        )}
      </Space>
    ),
    children: buildTreeData(node.children || [], level + 1),
  }));
};

const countNodes = (nodes) => {
  let count = 0;
  nodes.forEach(node => {
    count++;
    if (node.children && node.children.length > 0) {
      count += countNodes(node.children);
    }
  });
  return count;
};

const HierarchyTree = () => {
  const {
    hierarchyTree = [],
    unlinkedSSCCs = [],
    validation,
    summary,
    loading,
  } = useSelector((state) => state.hierarchy);

  if (loading) {
    return (
      <Card>
        <Text>Loading hierarchy...</Text>
      </Card>
    );
  }

  const totalNodes = countNodes(hierarchyTree);
  const hasUnlinked = unlinkedSSCCs.length > 0;

  return (
    <>
      {/* Validation Alert */}
      {validation && (
        <Alert
          type={validation.isValid ? "success" : "error"}
          message={
            <Space>
              <span>{validation.message}</span>
              {validation.rule && (
                <Tag color={validation.isValid ? "green" : "red"}>
                  {validation.rule}
                </Tag>
              )}
              {summary && (
                <>
                  <Tag color="blue">
                    Children: {summary.childCount}
                  </Tag>
                  {summary.linkedCount !== undefined && (
                    <Tag color="cyan">
                      Linked: {summary.linkedCount}
                    </Tag>
                  )}
                </>
              )}
            </Space>
          }
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Connected Hierarchy */}
      <Card
        title={
          <Space>
            <span>Connected Hierarchy</span>
            {totalNodes > 0 && (
              <Tag color="blue">Total: {totalNodes} nodes</Tag>
            )}
            {summary?.linkedCount > 0 && (
              <Tag color="green">Linked: {summary.linkedCount}</Tag>
            )}
          </Space>
        }
        size="small"
      >
        {hierarchyTree.length > 0 ? (
          <Tree
            showLine
            defaultExpandAll
            treeData={buildTreeData(hierarchyTree)}
            style={{
              background: '#fafafa',
              padding: '16px',
              borderRadius: '8px',
            }}
          />
        ) : (
          <Empty 
            description={validation?.isValid === false ? "Invalid hierarchy configuration" : "No hierarchy created yet"}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Available For Mapping */}
      {hasUnlinked && (
        <>
          <Divider />
          
          <Card
            title={
              <Space>
                <span>Available For Mapping</span>
                <Tag color="orange">{unlinkedSSCCs.length} unlinked</Tag>
              </Space>
            }
            size="small"
          >
            <Space
              direction="vertical"
              style={{
                width: "100%",
              }}
              size="middle"
            >
              {unlinkedSSCCs.map((item) => (
                <Card
                  key={item.id || item.sscc_code}
                  size="small"
                  style={{
                    background: '#fffbe6',
                    borderColor: '#ffe58f',
                  }}
                >
                  <Space>
                    {getIcon(item.level, item.status)}
                    <Text>{item.sscc_code}</Text>
                    <Tag color={getTagColor(item.level)}>
                      {item.level || 'CS'}
                    </Tag>
                    {getStatusTag(item.status)}
                    {item.id && (
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        ID: {item.id.substring(0, 8)}...
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
            </Space>
          </Card>
        </>
      )}

      {/* Summary Stats */}
      {summary && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <Tag color="geekblue">
              Total SSCCs: {summary.totalSSCCs}
            </Tag>
            {summary.hasParent && (
              <Tag color="blue">
                Parent: {summary.parentSSCC?.code}
              </Tag>
            )}
            <Tag color="cyan">
              Child Count: {summary.childCount}
            </Tag>
          </Space>
        </div>
      )}
    </>
  );
};

export default HierarchyTree;